/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useMemo } from 'react';
import { ScreenReaderText, TableHead, TableBody, TableColumnHeaderCell, TableCell, Tag } from '@blend/alchemy';

import { BlendBridge } from 'blb-types/blendBridge';

import { NestedHeaderRow, NestedTable, EllipsisCell, StyledRow, TableColumnHeaderFlushedRightCell } from '../styles';
import MembersAdminActions from './MembersAdminActions';
import RemoveTeamMemberModal from '../RemoveTeamMemberModal';
import EditTeamMemberModal from '../EditTeamMemberModal';

const getInvitationStatusPill = (status: BlendBridge.API.GroupInvitationStatus | undefined) => {
  switch (status) {
    case 'ACCEPTED':
      return <Tag text="Accepted" variant="positive" />;
    case 'DECLINED':
      return <Tag text="Declined" variant="error" />;
    case 'SENT':
      return <Tag text="Invite Pending" variant="alert" />;
    default:
      return <></>;
  }
};

const doesTeamNeedInvitationColumn = (team: BlendBridge.API.SearchGroupRecord): boolean => {
  return team.assignments.some(assignment => assignment.status !== 'ACCEPTED');
};

interface MembersTableProps {
  isTeamAdmin: boolean;
  refetchTeams: () => Promise<void>;
  team: BlendBridge.API.SearchGroupRecord;
}

const MembersTable: React.FC<MembersTableProps> = ({ team, isTeamAdmin, refetchTeams }) => {
  const [memberToRemove, setMemberToRemove] = useState<BlendBridge.API.GroupAssignmentWithName>();
  const [memberToEdit, setMemberToEdit] = useState<BlendBridge.API.GroupAssignmentWithName>();
  const showInvitationColumn = useMemo(() => doesTeamNeedInvitationColumn(team), [team]);

  const handleRemoveMemberModalClose = async (shouldRefetchTeams?: boolean) => {
    setMemberToRemove(undefined);
    if (shouldRefetchTeams) {
      await refetchTeams();
    }
  };

  const handleEditMemberModalClose = async (shouldRefetchTeams?: boolean) => {
    setMemberToEdit(undefined);
    if (shouldRefetchTeams) {
      await refetchTeams();
    }
  };

  return (
    <>
      <ScreenReaderText id={`it-Teams-${team.id}-member-table-label`}>Members of team {team.name}</ScreenReaderText>
      <NestedTable
        ariaLabelledBy={`it-Teams-${team.id}-member-table-label`}
        canExpandMultiple={false}
        gridTemplateColumnsRowRule={`1fr 1fr ${showInvitationColumn ? '11rem' : ''} 6rem 10rem`}
      >
        <TableHead>
          <NestedHeaderRow rowKey="header">
            <TableColumnHeaderCell>Name</TableColumnHeaderCell>
            <TableColumnHeaderCell>Email</TableColumnHeaderCell>
            {showInvitationColumn && <TableColumnHeaderCell>Invitation Status</TableColumnHeaderCell>}
            <TableColumnHeaderCell>Admin</TableColumnHeaderCell>
            <TableColumnHeaderFlushedRightCell>Actions</TableColumnHeaderFlushedRightCell>
          </NestedHeaderRow>
        </TableHead>
        <TableBody>
          {team.assignments.map(assignment => (
            <StyledRow key={assignment.principalId} rowKey={assignment.principalId}>
              <EllipsisCell>{assignment.name}</EllipsisCell>
              <EllipsisCell>{assignment.username}</EllipsisCell>
              {showInvitationColumn && <TableCell>{getInvitationStatusPill(assignment.status)}</TableCell>}
              <TableCell>{assignment.role === 'ADMIN' ? 'Yes' : 'No'}</TableCell>
              <MembersAdminActions
                isTeamAdmin={isTeamAdmin}
                openEditMemberModal={() => {
                  setMemberToEdit(assignment);
                }}
                openRemoveMemberModal={() => {
                  setMemberToRemove(assignment);
                }}
                principalId={assignment.principalId}
                teamId={team.id}
              />
            </StyledRow>
          ))}
        </TableBody>
      </NestedTable>
      {memberToRemove && (
        <RemoveTeamMemberModal assignment={memberToRemove} handleClose={handleRemoveMemberModalClose} team={team} />
      )}
      {memberToEdit && (
        <EditTeamMemberModal assignment={memberToEdit} handleClose={handleEditMemberModalClose} team={team} />
      )}
    </>
  );
};

export default MembersTable;
